import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { useAuthStore } from './authStore';

interface RequirePermissionProps {
  permission?: string;
  /** Any-of: grant access when the user holds at least one of these permissions. */
  anyOf?: string[];
  children: ReactNode;
}

/**
 * Renders its children only when the current user holds the required permission (or any of the
 * `anyOf` permissions); otherwise shows a "not authorised" panel. This mirrors the server-side
 * permission check so the UI never offers an action the API would reject.
 */
export default function RequirePermission({ permission, anyOf, children }: RequirePermissionProps) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);

  const denied = (permission && !hasPermission(permission)) || (anyOf && !hasAnyPermission(...anyOf));

  if (denied) {
    const label = permission ?? anyOf?.join(' / ') ?? '';
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <CardContent
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 6,
            }}
          >
            <BlockOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              You don't have access to this area
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 440 }}>
              This module requires the <code>{label}</code> permission. Contact your administrator
              if you believe you should have access.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return <>{children}</>;
}
